import React, { useEffect, useState, useCallback } from 'react';
import { CreditCard, RefreshCw, Save, Building2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../services/api';
import { format } from 'date-fns';

const PLANS = [
  { value: 'trial', label: 'Trial', cls: 'bg-gray-100 text-gray-700' },
  { value: 'starter', label: 'Starter', cls: 'bg-blue-100 text-blue-700' },
  { value: 'pro', label: 'Pro', cls: 'bg-purple-100 text-purple-700' },
];

const planClass = (plan) => PLANS.find(p => p.value === plan)?.cls || 'bg-gray-100 text-gray-600';

const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState({}); // facilityId → { plan, expires_at, notes }
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest('/facilities/subscriptions');
      setRows(res.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditing(prev => ({
      ...prev,
      [row.facility_id]: {
        plan: row.plan || 'trial',
        expires_at: row.expires_at ? format(new Date(row.expires_at), 'yyyy-MM-dd') : '',
        notes: row.notes || '',
      },
    }));
  };

  const cancelEdit = (facilityId) => {
    setEditing(prev => { const n = { ...prev }; delete n[facilityId]; return n; });
  };

  const save = async (facilityId) => {
    const data = editing[facilityId];
    if (!data) return;
    setSaving(facilityId);
    try {
      await apiRequest(`/facilities/${facilityId}/subscription`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      cancelEdit(facilityId);
      await load();
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const setField = (facilityId, field, value) => {
    setEditing(prev => ({ ...prev, [facilityId]: { ...prev[facilityId], [field]: value } }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <CreditCard className="h-7 w-7 text-ga-primary" />
              Subscriptions
            </h1>
            <p className="text-ga-text-muted mt-1 text-sm">
              Manage facility plan tiers and license expiry dates ({rows.length} facilities)
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ga-border-subtle text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="py-16 text-center text-ga-text-muted">Loading…</div>
        ) : (
          <div className="ga-card rounded-xl border border-ga-border-subtle shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wide border-b border-ga-border-subtle">
                    <th className="px-4 py-3">Facility</th>
                    <th className="px-4 py-3">Admin user</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3">Notes</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ga-border-subtle">
                  {rows.map(row => {
                    const isEditing = !!editing[row.facility_id];
                    const draft = editing[row.facility_id] || {};
                    const expired = isExpired(row.expires_at);
                    return (
                      <tr key={row.facility_id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-ga-text-muted shrink-0" />
                            <div>
                              <div className="font-medium text-ga-text-primary">{row.facility_name}</div>
                              <div className="text-xs text-ga-text-muted">{row.city}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-ga-text-muted">
                          {row.admin_name || <span className="text-ga-text-subtle italic">No admin</span>}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <select
                              value={draft.plan}
                              onChange={e => setField(row.facility_id, 'plan', e.target.value)}
                              className="px-2 py-1 rounded border border-ga-border-subtle text-sm"
                            >
                              {PLANS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                          ) : (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planClass(row.plan)}`}>
                              {PLANS.find(p => p.value === row.plan)?.label || row.plan}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="date"
                              value={draft.expires_at}
                              onChange={e => setField(row.facility_id, 'expires_at', e.target.value)}
                              className="px-2 py-1 rounded border border-ga-border-subtle text-sm"
                            />
                          ) : row.expires_at ? (
                            <span className={expired ? 'text-red-600 font-medium' : 'text-ga-text-primary'}>
                              {format(new Date(row.expires_at), 'dd MMM yyyy')}
                              {expired && ' (expired)'}
                            </span>
                          ) : (
                            <span className="text-ga-text-subtle">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="text"
                              value={draft.notes}
                              onChange={e => setField(row.facility_id, 'notes', e.target.value)}
                              placeholder="Internal notes…"
                              className="px-2 py-1 rounded border border-ga-border-subtle text-sm w-full max-w-[200px]"
                            />
                          ) : (
                            <span className="text-ga-text-muted text-xs">{row.notes || '—'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => save(row.facility_id)}
                                disabled={saving === row.facility_id}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded bg-ga-primary text-white text-xs font-medium hover:bg-ga-primary/90 disabled:opacity-60"
                              >
                                <Save className="h-3.5 w-3.5" />
                                {saving === row.facility_id ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                onClick={() => cancelEdit(row.facility_id)}
                                className="text-xs text-ga-text-muted hover:text-ga-text-primary"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(row)}
                              className="text-xs font-medium text-ga-primary hover:underline"
                            >
                              Edit plan
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length === 0 && (
                <div className="py-12 text-center text-ga-text-muted">No facilities found.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
